import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Send, AlertCircle } from 'lucide-react';

export default function ContactPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = encodeURIComponent(
      `Name: ${form.name || '(not provided)'}\n` +
      `Email: ${form.email || '(not provided)'}\n\n` +
      `${form.message}`
    );
    const subject = encodeURIComponent(form.subject || 'BitBot Contact Form');
    window.location.href = `mailto:eviltim@gmail.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8" data-testid="contact-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Get in </span>
          <span className="text-[#00FF41]">Touch</span>
        </h1>
        <p className="text-gray-400 mb-8">
          Questions, feature requests, bug reports, or trading-strategy ideas — we read everything.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <a href="mailto:eviltim@gmail.com" className="border border-[#003B00] bg-[#0a0a0a] p-4 hover:border-[#00FF41] transition-colors" data-testid="contact-email-card">
            <Mail className="h-5 w-5 text-[#00FF41] mb-2" />
            <div className="text-sm text-[#00FF41] font-bold mb-1">Email</div>
            <div className="text-xs text-gray-400 break-all">eviltim@gmail.com</div>
          </a>
          <div className="border border-[#003B00] bg-[#0a0a0a] p-4">
            <MessageCircle className="h-5 w-5 text-[#00FF41] mb-2" />
            <div className="text-sm text-[#00FF41] font-bold mb-1">In-App Messages</div>
            <div className="text-xs text-gray-400">Logged-in users can use the Messages tab to chat with other traders or admins directly.</div>
          </div>
          <div className="border border-[#003B00] bg-[#0a0a0a] p-4">
            <AlertCircle className="h-5 w-5 text-[#00FF41] mb-2" />
            <div className="text-sm text-[#00FF41] font-bold mb-1">Response Time</div>
            <div className="text-xs text-gray-400">Most messages get a reply within 48 hours. Trading issues marked URGENT get same-day attention.</div>
          </div>
        </div>

        <section className="border border-[#003B00] bg-[#0a0a0a] p-6 mb-10">
          <h2 className="text-xl font-bold text-[#00FF41] mb-4">Send a message</h2>
          {submitted ? (
            <div className="text-sm text-green-400" data-testid="contact-sent">
              ✓ Your email client should have opened. If nothing happened, just email{' '}
              <a href="mailto:eviltim@gmail.com" className="text-[#00FF41] underline">eviltim@gmail.com</a> directly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Your name (optional)</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-black border border-[#003B00] focus:border-[#00FF41] text-white px-3 py-2 text-sm outline-none transition-colors"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Your email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-black border border-[#003B00] focus:border-[#00FF41] text-white px-3 py-2 text-sm outline-none transition-colors"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-black border border-[#003B00] focus:border-[#00FF41] text-white px-3 py-2 text-sm outline-none transition-colors"
                  data-testid="contact-subject-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Message *</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-black border border-[#003B00] focus:border-[#00FF41] text-white px-3 py-2 text-sm outline-none transition-colors resize-y"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                className="flex items-center gap-2 bg-[#00FF41] hover:bg-[#00CC33] text-black font-bold px-5 py-2 text-sm transition-colors"
                data-testid="contact-send-btn"
              >
                <Send className="h-4 w-4" /> Send message
              </button>
            </form>
          )}
        </section>

        <section className="space-y-3 text-sm text-gray-400">
          <p>
            For account-specific issues (login, billing, API key problems), please include your registered email
            address so we can look up your account quickly.
          </p>
          <p>
            For feature requests or feedback on trading strategy, screenshots and concrete examples are gold.
            The more detail, the better.
          </p>
          <p>
            We do not provide financial advice. If you're asking "should I buy X coin?", the answer is the same
            as it would be from any other website: do your own research. We're here to help with the platform,
            not your portfolio.
          </p>
        </section>

        <footer className="mt-12 pt-6 border-t border-[#003B00] text-xs text-gray-600 flex flex-wrap gap-4">
          <button onClick={() => navigate('/about')} className="hover:text-[#00FF41]">About</button>
          <button onClick={() => navigate('/faq')} className="hover:text-[#00FF41]">FAQ</button>
          <button onClick={() => navigate('/privacy')} className="hover:text-[#00FF41]">Privacy</button>
          <button onClick={() => navigate('/terms')} className="hover:text-[#00FF41]">Terms</button>
          <button onClick={() => navigate('/disclaimer')} className="hover:text-[#00FF41]">Disclaimer</button>
          <button onClick={() => navigate('/cookies')} className="hover:text-[#00FF41]">Cookies</button>
        </footer>
      </div>
    </div>
  );
}
